/**
 * Tech Stack Gap Appendix Slide
 * "Personal Tech Stacks Are Antiquated"
 * Enterprise vs Household comparison, SaaS spend stats, key insight
 * Sources: Zylo 2025, Vertice 2024, CloudZero 2024, Backlinko/data.ai 2024
 */

import React from 'react';
import { colors } from '@config/colors';
import {
  AppendixSlideLayout,
  AppendixContentArea,
  SlideHeader,
  AppendixColumn,
  AppendixSectionLabel,
  AppendixStatBar,
  AppendixStatNumber,
  AppendixStatDescription,
  AppendixStatSource,
  AppendixDataTable,
  AppendixDataRow,
  AppendixDataHeader,
  AppendixCellHighlight,
  AccentCalloutBox,
  AccentCalloutBody,
  AccentCalloutBold,
  DarkInsightBox,
  DarkInsightTitle,
  DarkInsightBody,
  DarkInsightEmphasis,
  AppendixSourceFooter,
} from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Component
// ============================================

export const TechStackGapSlide: React.FC = () => (
  <AppendixSlideLayout>
    <SlideHeader
      title="Personal Tech Stacks Are Antiquated"
      subtitle="Enterprise workers get $4,830–$8,700/yr in SaaS tooling — household managers get a shared Google Calendar"
    />

    <AppendixContentArea>
      <AppendixColumn>
        <AppendixSectionLabel>Enterprise vs. Household Comparison</AppendixSectionLabel>

        <AppendixDataTable>
          <AppendixDataHeader>
            <div>Dimension</div>
            <div>Enterprise Employee</div>
            <div>Household Manager</div>
          </AppendixDataHeader>
          <AppendixDataRow>
            <div>Annual tech spend</div>
            <AppendixCellHighlight $color={colors.accent.blue.exxtraDark}>$4,830&ndash;$8,700</AppendixCellHighlight>
            <AppendixCellHighlight $color={colors.accent.coral.exxtraDark}>~$0&ndash;$50</AppendixCellHighlight>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Number of apps</div>
            <div>112 actively used per org</div>
            <div>5&ndash;12 fragmented silos</div>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Integration layer</div>
            <div>SSO, APIs, Zapier, IT team</div>
            <AppendixCellHighlight $color={colors.accent.coral.exxtraDark}>Mom&rsquo;s working memory</AppendixCellHighlight>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>IT support</div>
            <div>Dedicated helpdesk</div>
            <div>Google + guesswork</div>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Onboarding</div>
            <div>Documented SOPs, training</div>
            <div>Tribal knowledge only</div>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Data portability</div>
            <div>Cloud-synced, backed up</div>
            <div>Scattered across devices</div>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Notifications</div>
            <div>Unified inbox, triaged</div>
            <div>Firehose across 10+ apps</div>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Permissions &amp; sharing</div>
            <div>Role-based access control</div>
            <div>Shared logins, no delegation</div>
          </AppendixDataRow>
        </AppendixDataTable>
      </AppendixColumn>

      <AppendixColumn>
        <AppendixSectionLabel>Enterprise SaaS Spending</AppendixSectionLabel>

        <AppendixStatBar $bg={`${colors.accent.blue.extraLight}50`} $accent={colors.accent.blue.exxtraDark}>
          <AppendixStatNumber $color={colors.accent.blue.exxtraDark}>$8.7K</AppendixStatNumber>
          <AppendixStatDescription>
            average SaaS spend per employee/year at mid-market companies.
            Ranges from $4,830 (enterprise) to $8,700 (SMB).{' '}
            <AppendixStatSource>(Zylo 2025 SaaS Mgmt Index)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar $bg={`${colors.accent.blue.extraLight}50`} $accent={colors.accent.blue.exxtraDark}>
          <AppendixStatNumber $color={colors.accent.blue.exxtraDark}>275</AppendixStatNumber>
          <AppendixStatDescription>
            average number of SaaS applications per organization &mdash;
            of which 112 are actively used. The rest are shelfware.{' '}
            <AppendixStatSource>(Vertice 2024)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar $bg={`${colors.accent.blue.extraLight}50`} $accent={colors.accent.blue.exxtraDark}>
          <AppendixStatNumber $color={colors.accent.blue.exxtraDark}>112</AppendixStatNumber>
          <AppendixStatDescription>
            actively used SaaS apps per company. Even &ldquo;small&rdquo; orgs
            average 50+ tools with dedicated integration layers.{' '}
            <AppendixStatSource>(CloudZero 2024)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AccentCalloutBox $bg={`${colors.accent.coral.exxtraLight}30`} $accent={colors.accent.coral.exxtraDark}>
          <AccentCalloutBody>
            <AccentCalloutBold $color={colors.accent.coral.exxtraDark}>The waste gap tells the story:</AccentCalloutBold>{' '}
            Enterprises waste an average of <AccentCalloutBold $color={colors.accent.coral.exxtraDark}>$135,000/yr on unused SaaS</AccentCalloutBold> &mdash;
            more than the entire lifetime tech spend of most households. Companies
            over-invest in employee productivity. Families get zero investment in
            the person managing $145K of invisible labor.
          </AccentCalloutBody>
        </AccentCalloutBox>

        <DarkInsightBox>
          <DarkInsightTitle>Key Insight</DarkInsightTitle>
          <DarkInsightBody>
            The technology doesn&rsquo;t exist &mdash; not that moms won&rsquo;t pay for it.{' '}
            <DarkInsightEmphasis>No one has built the household operating system</DarkInsightEmphasis> that
            treats family management as the complex, high-stakes operation it actually is.
            The consumer app store has 8.93M apps (Backlinko/data.ai 2024), yet none integrate
            the 35+ weekly decisions across 10+ life domains into a single intelligent layer.
          </DarkInsightBody>
        </DarkInsightBox>
      </AppendixColumn>
    </AppendixContentArea>

    <AppendixSourceFooter>
      Zylo SaaS Management Index (2025) &middot; Vertice SaaS Statistics (2024) &middot;
      CloudZero SaaS Spend Report (2024) &middot; Backlinko / data.ai App Store Statistics (2024) &middot;
      Productiv Enterprise SaaS Benchmarks
    </AppendixSourceFooter>
  </AppendixSlideLayout>
);
